import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import { Sparkle, ChatCircleDots, Crown, MapTrifold, FilmStrip, ShootingStar, ArrowRight, SealCheck } from "@phosphor-icons/react";

const FEATURES = [
  {
    to: "/astro-visualization",
    icon: ShootingStar,
    title: "Astro Visualization",
    tag: "World's first",
    body: "Your entire life rendered as an interactive, cinematic Mahadasha journey — scrub year by year through past and future Dashas and watch career, wealth and relationship chapters unfold visually.",
    testid: "showcase-card-astro-visualization",
  },
  {
    to: "/astrologers",
    icon: ChatCircleDots,
    title: "Specialist AI Astrologers",
    tag: "Live consultation",
    body: "Each AI expert masters one classical technique — KP Sub-Lord, Bhrigu Nandi Nadi, Tajaka Varshaphal, D9 Navamsa marriage — and answers from your live Swiss-Ephemeris chart in seconds. Not a static horoscope; a real consultation.",
    testid: "showcase-card-ai-astrologers",
  },
  {
    to: "/celebrities",
    icon: Crown,
    title: "Celebrity Chart Intelligence",
    tag: "1,500+ verified charts",
    body: "A proprietary database of 1,500+ celebrity horoscopes with rise-event Dasha analysis — see the exact Dasha that catapulted billionaires, lottery winners and world leaders.",
    testid: "showcase-card-celebrities",
  },
  {
    to: "/tools/celebrity-lookalike",
    icon: SealCheck,
    title: "Celebrity Lookalike Engine",
    tag: "Chart matching",
    body: "Match your own birth chart against the celebrity database and discover whose karmic blueprint mirrors yours.",
    testid: "showcase-card-lookalike",
  },
  {
    to: "/life-replay",
    icon: FilmStrip,
    title: "Life Replay",
    tag: "Karmic timeline",
    body: "A cinematic reconstruction of your past turning points, computed from Dasha × transit intersections — verify the engine against your own life before trusting its future.",
    testid: "showcase-card-life-replay",
  },
  {
    to: "/astro-gps",
    icon: MapTrifold,
    title: "Astro GPS · Astrocartography",
    tag: "150-city scoring",
    body: "Planetary lines and relocation zones scored across 150 cities for your exact goal — money, love, peace, fame or spirituality.",
    testid: "showcase-card-astro-gps",
  },
  {
    to: "/twenty-year-prediction",
    icon: Sparkle,
    title: "20-Year Vedic Forecast",
    tag: "55+ page PDF",
    body: "A year-by-year, computation-heavy report across career, marriage, wealth and health — with Sade Sati windows, dosha screening and gem remedies.",
    testid: "showcase-card-forecast",
  },
];

export default function AppShowcasePage() {
  return (
    <PageShell>
      <SEO
        title="Signature Features — AstroKuberChat"
        description="A guided tour of what makes AstroKuberChat unlike any other astrology app: the world's first Astro Visualization platform, specialist AI astrologers, 1,500+ celebrity charts and more."
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="showcase-page">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Sparkle size={14} weight="duotone" /> Signature features · a guided tour
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">
            The world's first <span className="text-gold">Astro Visualization</span> platform.
          </h1>
          <p className="text-muted text-base sm:text-lg mt-4 max-w-3xl font-light leading-relaxed">
            AstroKuberChat is not a daily-horoscope reader. It is a computation platform:
            proprietary Vedic engines render your life as an interactive visual journey,
            match your chart against 1,500+ verified celebrity horoscopes, score 150 cities
            for your destiny — and then let you talk it through with technique-specialist
            AI astrologers or live human experts, around the clock.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {FEATURES.map((f) => (
            <Link
              key={f.to}
              to={f.to}
              className="group rounded-2xl border border-soft bg-white/60 hover:bg-amber-50/60 hover:border-amber-300 p-5 sm:p-6 transition-colors flex flex-col"
              data-testid={f.testid}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-800">
                  <f.icon size={20} weight="duotone" />
                </span>
                <span className="text-[11px] font-bold tracking-wide uppercase text-amber-700 bg-amber-100 rounded-full px-2.5 py-1">
                  {f.tag}
                </span>
              </div>
              <h2 className="font-serif-display text-2xl font-light mt-4 text-[#3F2D1B]">{f.title}</h2>
              <p className="text-sm text-muted mt-2 leading-relaxed font-light flex-1">{f.body}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 mt-4 group-hover:gap-2.5 transition-all">
                Open feature <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
